import { type ComunicazioneDTO } from '@framework/api-client/api/backoffice/schemas/comunicazioneDTO.ts';
import { type ComunicazioneManualeRequest } from '@framework/api-client/api/backoffice/schemas/comunicazioneManualeRequest.ts';
import { type AllegatoComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/allegatoComunicazioneDTO.ts';
import { type ComunicazioneRequest } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneRequest.ts';
import { EnumDestinatarioDTOTipoDestinatario } from '@framework/api-client/api/inquiries-compile/schemas/enumDestinatarioDTOTipoDestinatario.ts';
import { EnumDestinatarioTipoDestinatarioEnum } from '@framework/api-client/api/inquiries-compile/schemas/enumDestinatarioTipoDestinatarioEnum.ts';
import { EnumTipoMittenteDTOCodice } from '@framework/api-client/api/inquiries-compile/schemas/enumTipoMittenteDTOCodice.ts';
import { type TipoComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoComunicazioneDTO.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { yup } from '@framework/validation/initYup.ts';
import { CommunicationLogic } from '../../ui/communication/logic/CommunicationLogic.ts';

const CommunicationAttachmentRequestSchema: yup.ObjectSchema<AllegatoComunicazioneDTO> =
  yup
    .object({
      id: yup
        .string()
        .required()
        .label(t('communication-request-schema.attachment-id', 'ID Allegato')),
      nomeFile: yup
        .string()
        .notRequired()
        .label(
          t('communication-request-schema.attachment-name', 'Nome Allegato'),
        ),
      // Essendo un campo "in sola lettura" e non inviato al BE, possiamo
      // evitare di implementare l'intera validazione
      tipoComunicazioneFileAllegato: yup
        .object()
        .notRequired()
        .label(
          t('communication-request-schema.attachment-type', 'Tipo Allegato'),
        ),
      // Retrocompatibilità con il vecchio schema
      tipoComunicazioneFileAllegatoDTO: yup.mixed().notRequired(),
    })
    .required()
    .noUnknown()
    .label(t('communication-request-schema.attachment', 'Allegato'));

type ICreateCommunicationRequest = ComunicazioneManualeRequest &
  ComunicazioneRequest & {
    isManual: boolean | null | undefined;
    communicationType: TipoComunicazioneDTO | null | undefined;
    // Flag inserito per poter essere passato alla onSubmit, così da gestire
    // l'azione di salvataggio. Se true, la onSubmit salva la comunicazione
    // senza altre operazioni (ricarica, selezione, message...)
    saveOnlyFlag: boolean | null | undefined;
  };

export const CreateCommunicationRequestSchema = yup
  .object({
    idProcedimentoMittente: yup
      .string()
      .notRequired()
      .label(
        t(
          'communication-request-schema.idProcedimentoMittente',
          'Endoprocedimento Mittente',
        ),
      )
      .when(['communicationType'], ([communicationType], schema) => {
        // Per tutti i tipi di mittenti ad esclusione degli endoprocedimenti, questo campo non è richiesto
        if (
          communicationType?.tipoMittente?.codice !==
          EnumTipoMittenteDTOCodice.ENDOPROCEDIMENTI_PRIMO_LIVELLO
        ) {
          return schema.notRequired().strip();
        }
        // In caso di mittente di tipo endoprocedimento, il campo è richiesto
        return schema.required();
      }),
    idTipoComunicazione: yup
      .string()
      .notRequired()
      .label(
        t(
          'communication-request-schema.idTipoComunicazione',
          'Tipo Comunicazione',
        ),
      )
      .when(['$isEdit'], ([isEdit], schema) => {
        return !isEdit ? schema.required() : schema.notRequired().strip();
      }),
    idComunicazione: yup
      .string()
      .notRequired()
      .label(
        t('communication-request-schema.idComunicazione', 'ID Comunicazione'),
      ),
    dataProtocollo: yup
      .string()
      .stringDate('YYYY-MM-DD[T]HH:mm:ss')
      .notRequired()
      .when(['isManual'], ([isManual], schema) =>
        isManual ? schema.required() : schema.notRequired().strip(),
      )
      .label(
        t('communication-request-schema.dataProtocollo', 'Data Protocollo'),
      ),
    numeroProtocollo: yup
      .string()
      .notRequired()
      .when(['isManual'], ([isManual], schema) =>
        isManual ? schema.required() : schema.notRequired().strip(),
      )
      .label(
        t('communication-request-schema.numeroProtocollo', 'Numero Protocollo'),
      ),
    oggetto: yup
      .string()
      .notRequired()
      .when(['isManual'], ([isManual], schema) =>
        isManual ? schema.required() : schema.notRequired(),
      )
      .when(['communicationType', 'messaggio'], (input, schema) => {
        const [communicationType, messaggio] = input as [
          TipoComunicazioneDTO | null | undefined,
          string | null | undefined,
        ];
        if (!communicationType?.flOggettoEditabile) {
          return schema.notRequired();
        }
        return schema
          .notRequired()
          .test(
            'message-object-required',
            t(
              'communication-request-schema.message-required',
              'È obbligatorio compilare almeno uno tra messaggio e oggetto',
            ),
            value => {
              return Boolean(value) || Boolean(messaggio);
            },
          );
      })
      .label(t('communication-request-schema.oggetto', 'Oggetto')),
    messaggio: yup
      .string()
      .notRequired()
      .when(['isManual'], ([isManual], schema) =>
        isManual ? schema.required() : schema.notRequired(),
      )
      .label(t('communication-request-schema.messaggio', 'Messaggio')),
    destinatari: yup
      .array()
      .of(
        yup
          .object({
            tipoDestinatario: yup
              .string()
              .oneOfEnum(EnumDestinatarioDTOTipoDestinatario)
              .required()
              .label(
                t(
                  'communication-request-schema.idDestinatario',
                  'Tipo Destinatario',
                ),
              ),
            idEndoprocedimento: yup
              .string()
              .notRequired()
              .label(
                t(
                  'communication-request-schema.idEndoprocedimento',
                  'Endoprocedimento',
                ),
              ),
          })
          .required()
          .noUnknown()
          .label(
            t('communication-request-schema.destinatario', 'Destinatario'),
          ),
      )
      .notRequired()
      .default([])
      .min(
        1,
        t(
          'communication-request-schema.destinatario-required',
          'È richiesto selezionare almeno un destinatario',
        ),
      )
      .label(t('communication-request-schema.destinatari', 'Destinatari')),
    allegati: yup
      .array(CommunicationAttachmentRequestSchema)
      .notRequired()
      .when(
        ['communicationType'],
        (
          [communicationType]: Array<TipoComunicazioneDTO | null | undefined>,
          schema,
        ) => {
          if (!communicationType?.tipoComunicazioneFileAllegati?.length) {
            return schema.notRequired().transform(() => []);
          }
          return schema.notRequired();
        },
      )
      .default([]),
    noteAddizionali: yup
      .string()
      .notRequired()
      .when(['isManual'], ([isManual], schema) => {
        return isManual ? schema.notRequired().strip() : schema.notRequired();
      })
      .when(
        ['communicationType'],
        (
          [communicationType]: Array<TipoComunicazioneDTO | null | undefined>,
          schema,
        ) => {
          if (!communicationType?.flNoteAddizionali) {
            return schema.notRequired().strip();
          }
          return schema;
        },
      )
      .label(
        t('communication-request-schema.noteAddizionali', 'Note Addizionali'),
      ),
    inoltro: yup
      .object({
        idComunicazione: yup
          .string()
          .required()
          .label(
            t(
              'communication-request-schema.inoltro.idComunicazione',
              'ID Comunicazione',
            ),
          ),
        messaggio: yup
          .string()
          .notRequired()
          .label(
            t('communication-request-schema.inoltro.messaggio', 'Messaggio'),
          ),
        allegati: yup
          .array(
            yup
              .object({
                idAllegato: yup.string().required(),
                allegato: yup.mixed().required(),
              })
              .required()
              .noUnknown(),
          )
          .notRequired()
          .default([])
          .label(
            t(
              'communication-request-schema.inoltro.allegati',
              'Allegati Comunicazione',
            ),
          ),
      })
      .notRequired()
      .default(null),
    flMessaggioModificato: yup
      .boolean()
      .notRequired()
      .label(
        t(
          'communication-request-schema.flMessaggioModificato',
          'Messaggio Modificato',
        ),
      )
      .default(false),
    flOggettoModificato: yup
      .boolean()
      .notRequired()
      .label(
        t(
          'communication-request-schema.flOggettoModificato',
          'Oggetto Modificato',
        ),
      )
      .default(false),

    // Oggetti non presenti nella richiesta della comunciazione ma
    // aggiungi in creazione del form per gestire la validazione.
    isManual: yup.boolean().notRequired().default(false),
    // Essendo un campo che viene viene solo impostato e letto,
    // e non viene inviato a BE, possiamo evitare di implementare
    // l'intera validazione
    communicationType: yup
      .object<TipoComunicazioneDTO>()
      .notRequired()
      .label(
        'Dettaglio tipo comunicazione',
      ) as yup.ObjectSchema<TipoComunicazioneDTO>,
    saveOnlyFlag: yup.boolean().notRequired(),
  })
  // In questo caso mettiamo satisfies così da prendere il tipo giusto nella infer qui sotto.
  // Se usassimo la definizione "schema: yup.objectScema<...>" il tipo verrebbe sovrascritto con
  // quello dell'interface (che è soddisfatta ma è diverso, non contiene campi come i dati extra
  // degli allegati).
  .noUnknown() satisfies yup.ObjectSchema<ICreateCommunicationRequest>;

export type CreateCommunicationRequest = yup.InferType<
  typeof CreateCommunicationRequestSchema
>;

export class CreateCommunicationRequestMapper {
  static requestToFormData(
    communication: ComunicazioneDTO,
  ): CreateCommunicationRequest {
    return CreateCommunicationRequestSchema.cast(
      {
        // Potrebbe essere già impostato dalla comunicazione ricevuta,
        // in caso di creazione, quindi è prima dello spread.
        idProcedimentoMittente:
          communication.procedimentoMittente?.idProcedimento,

        ...communication,

        idComunicazione: communication.id,
        idTipoComunicazione: communication.tipoComunicazione?.id,

        destinatari: CommunicationLogic.allRecipients(communication).map(
          recipient => ({
            tipoDestinatario: recipient.tipoDestinatarioEnum,
            idEndoprocedimento:
              recipient.tipoDestinatarioEnum ==
              EnumDestinatarioTipoDestinatarioEnum.ENDOPROCEDIMENTI_PRIMO_LIVELLO
                ? recipient.idProcedimento
                : null,
          }),
        ),
        // Dati di protocollo
        numeroProtocollo:
          communication.destinatariProcedimenti?.[0]?.numeroProtocollo, // (?) communication.numeroProtocolloUscita
        dataProtocollo: communication.dataInvio, // (?) communication.dataRegistrazioneUscita

        isManual: communication.flCaricataManualmente,
        communicationType: communication.tipoComunicazione,
      },
      {
        assert: false,
      },
    );
  }

  static formDataToRequest(
    data: CreateCommunicationRequest,
  ): ComunicazioneManualeRequest & ComunicazioneRequest {
    const { isManual, communicationType, allegati, saveOnlyFlag, ...rest } =
      data;

    return {
      ...rest,
      // Rimappiamo gli allegati rimuovendo i dati extra da non inviare al BE
      allegati: allegati?.map(a => ({ id: a.id ?? '<unknown>' })) ?? [],
    };
  }
}
